import { useEffect, useState } from "react";
import axios from "axios";
import { Camera, MessageCircle, Lock, LogOut } from "lucide-react";
import Seo from "@/components/Seo";
import { waLink } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "fumiga_panel_token";

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("es-BO", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const PanelPage = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [consultas, setConsultas] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [leads, setLeads] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    const onError = (e) => {
      if (e?.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
        setError("La sesión venció. Entrá de nuevo.");
      }
    };
    axios.get(`${API}/consultas`, { headers }).then((r) => setConsultas(r.data)).catch(onError);
    axios.get(`${API}/quotes`, { headers }).then((r) => setQuotes(r.data)).catch(onError);
    axios.get(`${API}/leads`, { headers }).then((r) => setLeads(r.data)).catch(onError);
  }, [token]);

  const login = async () => {
    if (!password || loading) return;
    setLoading(true);
    setError("");
    try {
      const r = await axios.post(`${API}/panel/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, r.data.token);
      setToken(r.data.token);
      setPassword("");
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === "string" ? d : "No se pudo entrar. Probá de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  if (!token) {
    return (
      <div className="bg-[#151A1F] min-h-screen pt-[72px] flex items-center justify-center">
        <Seo title="Panel de campo | Fumiga" siteName="Fumiga" description="Panel interno de consultas y cotizaciones." />
        <div className="hairlines absolute inset-0 pointer-events-none" aria-hidden="true" />
        <div className="relative w-full max-w-sm mx-6 border border-white/10 bg-white/[0.03] p-8 sm:p-10" data-testid="panel-login">
          <div className="w-12 h-12 border border-[#00A651]/40 flex items-center justify-center">
            <Lock size={20} className="text-[#00A651]" />
          </div>
          <h1 className="font-display font-bold tracking-tight text-2xl mt-6">Panel de campo</h1>
          <p className="mt-2 text-sm text-[#F6F7F5]/55">Zona interna de Fumiga. Solo con clave.</p>
          <label className="block mt-8">
            <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#F6F7F5]/50">Clave del panel</span>
            <input
              data-testid="panel-login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && login()}
              placeholder="••••••••"
              className="mt-2 w-full bg-white/[0.04] border border-white/15 rounded-sm px-4 py-3.5 text-base text-[#F6F7F5] placeholder:text-[#F6F7F5]/30 focus:outline-none focus:border-[#00A651] transition-colors"
            />
          </label>
          {error && (
            <p data-testid="panel-login-error" className="mt-3 text-sm text-[#FFB000]">{error}</p>
          )}
          <button
            data-testid="panel-login-submit"
            onClick={login}
            disabled={loading || !password}
            className="mt-6 w-full bg-[#FFB000] text-[#151A1F] font-display font-semibold text-base px-6 py-3.5 rounded-full hover:scale-[1.02] transition-transform duration-300 disabled:opacity-60"
          >
            {loading ? "Entrando..." : "Entrar al panel"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#151A1F] min-h-screen pt-[72px]">
      <Seo title="Panel de campo | Fumiga" siteName="Fumiga" description="Panel interno de consultas y cotizaciones." />
      <div className="mx-auto max-w-7xl px-6 sm:px-10 py-16 sm:py-20">
        <div className="flex items-start justify-between gap-6">
          <div>
            <p className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#00A651]">Panel interno · Diego</p>
            <h1 className="font-display font-bold tracking-tight text-3xl sm:text-5xl mt-4">Qué está pasando en el campo.</h1>
            <p className="mt-4 text-[#F6F7F5]/60 max-w-xl">
              Cada consulta de la IA y cada cotización queda registrada acá, con su zona y su cultivo.
            </p>
          </div>
          <button
            data-testid="panel-logout"
            onClick={logout}
            className="shrink-0 inline-flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[#F6F7F5]/50 hover:text-[#FFB000] border border-white/15 hover:border-[#FFB000]/50 rounded-full px-4 py-2 transition-colors"
          >
            <LogOut size={14} /> Salir
          </button>
        </div>

        <div className="mt-14">
          <h2 className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#FFB000]" data-testid="panel-quotes-title">
            Cotizaciones ({quotes.length})
          </h2>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {quotes.length === 0 && (
              <p className="col-span-full bg-[#151A1F] p-8 text-[#F6F7F5]/50 text-sm">Todavía no hay cotizaciones.</p>
            )}
            {quotes.map((q) => (
              <article key={q.id} data-testid={`panel-quote-${q.id}`} className="bg-[#151A1F] p-6">
                <p className="font-display font-bold text-xl">{q.cultivo}</p>
                <p className="mt-1 text-sm text-[#F6F7F5]/60">{q.zona} · {q.hectareas} ha</p>
                <p className="mt-1 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[#F6F7F5]/35">{fmt(q.created_at)}</p>
                <a
                  data-testid={`panel-quote-wa-${q.id}`}
                  href={`https://wa.me/${q.whatsapp.replace(/[^0-9]/g, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-2 text-sm text-[#00A651] hover:gap-3 transition-all"
                >
                  <MessageCircle size={16} /> {q.whatsapp}
                </a>
              </article>
            ))}
          </div>
        </div>

        <div className="mt-16">
          <h2 className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#F6F7F5]" data-testid="panel-leads-title">
            Descargas de la guía ({leads.length})
          </h2>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {leads.length === 0 && (
              <p className="col-span-full bg-[#151A1F] p-8 text-[#F6F7F5]/50 text-sm">Todavía nadie dejó su WhatsApp por la guía.</p>
            )}
            {leads.map((l) => (
              <article key={l.id} data-testid={`panel-lead-${l.id}`} className="bg-[#151A1F] p-6">
                <p className="font-display font-bold text-lg">{l.whatsapp || "Sin número"}</p>
                <p className="mt-1 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[#F6F7F5]/35">{fmt(l.created_at)}</p>
                {l.whatsapp && (
                  <a
                    data-testid={`panel-lead-wa-${l.id}`}
                    href={`https://wa.me/${l.whatsapp.replace(/[^0-9]/g, "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center gap-2 text-sm text-[#00A651] hover:gap-3 transition-all"
                  >
                    <MessageCircle size={16} /> Escribirle
                  </a>
                )}
              </article>
            ))}
          </div>
        </div>

        <div className="mt-16">
          <h2 className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#00A651]" data-testid="panel-consultas-title">
            Consultas de la IA ({consultas.length})
          </h2>
          <div className="mt-6 space-y-4">
            {consultas.length === 0 && (
              <p className="text-[#F6F7F5]/50 text-sm">Todavía no hay consultas.</p>
            )}
            {consultas.map((c) => (
              <details key={c.id} data-testid={`panel-consulta-${c.id}`} className="group border border-white/10 bg-white/[0.02] open:bg-white/[0.04] transition-colors">
                <summary className="cursor-pointer list-none p-6 flex flex-wrap items-center gap-x-6 gap-y-2">
                  <span className="font-display font-bold text-lg">{c.cultivo}</span>
                  {c.ubicacion && <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[#FFB000]">{c.ubicacion}</span>}
                  {c.con_foto && (
                    <span className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] uppercase tracking-[0.15em] text-[#00A651]">
                      <Camera size={12} /> con foto
                    </span>
                  )}
                  <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[#F6F7F5]/35 ml-auto">{fmt(c.created_at)}</span>
                  <p className="w-full text-sm text-[#F6F7F5]/60 mt-1">{c.problema}</p>
                </summary>
                <div className="px-6 pb-6 text-sm text-[#F6F7F5]/75 leading-relaxed whitespace-pre-wrap border-t border-white/10 pt-4">
                  {c.orientacion.replace(/\*\*/g, "")}
                </div>
              </details>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PanelPage;
